import type { QuizItem } from "../types";
import { WECHAT_MINI_OPENLINK_MUSIC_LINES, WECHAT_MINI_OPENLINK_PAINTING_MATCH } from "../config";
import {
  DESC_MUSIC_LINES_MINI,
  DESC_PAINTING_MATCH_MINI,
  wechatOpenButtonHtml,
} from "../copy/wechat-miniprograms";
import { escapeHtml } from "../util/html";
import { resolveAudioUrl } from "../util/audio";
import { deferForFastPaint } from "../util/defer";

export function renderGames(): string {
  return `
    <h1 class="section-title" style="margin-top:0">互动游戏</h1>
    <p class="section-sub">本站提供古典音乐测歌名/古典名画对对配/古典音乐线条等互动游戏；古典名画对对配/古典音乐线条两款玩法请在微信中搜索并打开对应小程序。</p>

    <section class="quiz-section" aria-labelledby="quiz-title">
      <h2 id="quiz-title" class="detail-heading">测歌名</h2>
      <div id="quiz-root" class="quiz-root">
        <p class="section-sub">加载题库…</p>
      </div>
    </section>

    <section class="mini-intro-section" aria-labelledby="music-lines-title">
      <h2 id="music-lines-title" class="detail-heading">古典音乐线条</h2>
      <p class="section-sub game-mini-desc">${escapeHtml(DESC_MUSIC_LINES_MINI)}</p>
      ${wechatOpenButtonHtml(WECHAT_MINI_OPENLINK_MUSIC_LINES)}
    </section>

    <section class="mini-intro-section" aria-labelledby="painting-match-title">
      <h2 id="painting-match-title" class="detail-heading">古典名画对对配</h2>
      <p class="section-sub game-mini-desc">${escapeHtml(DESC_PAINTING_MATCH_MINI)}</p>
      ${wechatOpenButtonHtml(WECHAT_MINI_OPENLINK_PAINTING_MATCH)}
    </section>
  `;
}

function quizHtml(
  q: QuizItem,
  index: number,
  total: number,
  state: "idle" | "answered",
  picked: string | undefined,
  sourceLabel: string
): string {
  const src = resolveAudioUrl(q.audioSnippet);
  const opts = q.options
    .map((opt) => {
      const wrong = state === "answered" && picked === opt && opt !== q.answer;
      const right = state === "answered" && opt === q.answer;
      const cls = wrong ? "quiz-opt wrong" : right ? "quiz-opt right" : "quiz-opt";
      const dis = state === "answered" ? "disabled" : "";
      return `<button type="button" class="${cls}" ${dis} data-answer="${escapeHtml(opt)}">${escapeHtml(opt)}</button>`;
    })
    .join("");

  const feedback =
    state === "answered"
      ? `<div class="quiz-feedback">
           <p><strong>${picked === q.answer ? "答对了" : "再想想"}</strong> — ${escapeHtml(q.explanation ?? "")}</p>
           <button type="button" class="pill-btn" id="quiz-next">下一题</button>
         </div>`
      : q.hint
        ? `<p class="quiz-hint">提示：${escapeHtml(q.hint)}</p>`
        : "";

  return `
    <div class="quiz-card">
      <p class="quiz-progress">第 ${index + 1} / ${total} 题 · ${escapeHtml(sourceLabel)}</p>
      <p class="quiz-question">${escapeHtml(q.question)}</p>
      <div class="audio-block">
        <audio id="quiz-audio" controls preload="metadata" src="${escapeHtml(src)}"></audio>
      </div>
      <div class="quiz-options" role="group" aria-label="选项">${opts}</div>
      ${feedback}
    </div>
  `;
}

async function loadQuizItems(): Promise<{ items: QuizItem[]; sourceLabel: string }> {
  const localRes = await fetch("/data/quiz-local.json");
  if (localRes.ok) {
    const local = (await localRes.json()) as { quizzes?: QuizItem[] };
    const q = local.quizzes ?? [];
    if (q.length > 0) return { items: q, sourceLabel: "节选题库" };
  }
  const res = await fetch("/data/quiz.json");
  if (!res.ok) throw new Error(String(res.status));
  const data = (await res.json()) as { quizzes: QuizItem[] };
  const all = data.quizzes ?? [];
  return { items: all, sourceLabel: "完整题库" };
}

export function hydrateQuiz(container: HTMLElement): void {
  deferForFastPaint(() => {
    void hydrateQuizAsync(container);
  });
}

async function hydrateQuizAsync(container: HTMLElement): Promise<void> {
  const root = container.querySelector("#quiz-root");
  if (!root) return;

  try {
    const { items: all, sourceLabel } = await loadQuizItems();
    if (!root.isConnected) return;
    if (all.length === 0) {
      root.innerHTML =
        '<p class="section-sub">暂时没有可用题目，请稍后再试。</p>';
      return;
    }

    const order = shuffleIndices(all.length).slice(0, Math.min(16, all.length));
    let pos = 0;
    let answered = false;
    let picked: string | undefined;

    const render = () => {
      if (!root.isConnected) return;
      const q = all[order[pos]]!;
      root.innerHTML = quizHtml(q, pos, order.length, answered ? "answered" : "idle", picked, sourceLabel);
      const audio = root.querySelector<HTMLAudioElement>("#quiz-audio");

      root.querySelectorAll<HTMLButtonElement>(".quiz-opt").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (answered) return;
          picked = btn.getAttribute("data-answer") ?? "";
          answered = true;
          render();
          void audio?.pause();
        });
      });

      root.querySelector("#quiz-next")?.addEventListener("click", () => {
        pos = (pos + 1) % order.length;
        answered = false;
        picked = undefined;
        render();
      });
    };

    render();
  } catch {
    if (root.isConnected) {
      root.innerHTML =
        '<p class="section-sub">题库暂时无法加载，请稍后再试。</p>';
    }
  }
}

function shuffleIndices(n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
